import React, { useState } from 'react';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { FaceSmileIcon, FaceFrownIcon, ShieldExclamationIcon, ArrowTrendingUpIcon, ArrowTrendingDownIcon, MinusIcon, LightBulbIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const SENTIMENT_COLORS = { positive: '#22c55e', negative: '#ef4444', neutral: '#f59e0b', mixed: '#8b5cf6' };

export default function BrandSentimentAnalysis({ data }) {
  const [activeTab, setActiveTab] = useState('overview');
  if (!data) return null;

  const sentimentColor = SENTIMENT_COLORS[data.overallSentiment] || SENTIMENT_COLORS.neutral;
  const trendIcon = data.sentimentTrend === 'improving' ? ArrowTrendingUpIcon : data.sentimentTrend === 'declining' ? ArrowTrendingDownIcon : MinusIcon;
  const TrendIcon = trendIcon;
  const trendColor = data.sentimentTrend === 'improving' ? 'text-green-700' : data.sentimentTrend === 'declining' ? 'text-red-700' : 'text-yellow-700';

  // Prepare emotion radar data
  const emotionEntries = data.emotionalTone ? Object.entries(data.emotionalTone) : [];
  const emotionData = emotionEntries
    .filter(([, val]) => val && val.measured && typeof val.value === 'number')
    .map(([key, val]) => ({
      emotion: key.charAt(0).toUpperCase() + key.slice(1),
      value: val.value,
      fullMark: 100
    }));
  const unmeasuredEmotions = emotionEntries
    .filter(([, val]) => !val || !val.measured)
    .map(([key]) => key.charAt(0).toUpperCase() + key.slice(1));

  // Sentiment distribution pie data
  const distData = [
    { name: 'Positive', value: data.distribution?.positive || 0, color: '#22c55e' },
    { name: 'Negative', value: data.distribution?.negative || 0, color: '#ef4444' },
    { name: 'Neutral', value: data.distribution?.neutral || 0, color: '#f59e0b' }
  ];

  // Sentiment drivers bar data
  const driverData = (data.sentimentDrivers || []).map(d => ({
    name: d.factor,
    strength: d.strength,
    fill: d.impact === 'positive' ? '#22c55e' : d.impact === 'negative' ? '#ef4444' : '#f59e0b'
  }));

  const tabClass = (t) => `flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${activeTab === t ? 'text-orange-400 border-b-2 border-orange-500 bg-gray-50' : 'text-gray-600 hover:text-gray-200'}`;

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="bg-gray-100/60 backdrop-blur rounded-2xl p-6 border border-gray-300/50">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
          {/* Sentiment Gauge */}
          <div className="flex flex-col items-center">
            <div className="relative w-28 h-28">
              <svg className="w-28 h-28 transform -rotate-90" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="50" fill="none" stroke="#374151" strokeWidth="10" />
                <circle cx="60" cy="60" r="50" fill="none" stroke={sentimentColor} strokeWidth="10"
                  strokeDasharray={`${((data.sentimentScore || 0) / 100) * 314} 314`} strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl font-bold text-gray-900">{data.sentimentScoreMeasured === false ? 'N/M' : data.sentimentScore}</span>
                <span className="text-xs text-gray-600">{data.sentimentScoreMeasured === false ? '' : '/100'}</span>
              </div>
            </div>
            <span className="text-sm text-gray-600 mt-1">Sentiment Score</span>
          </div>

          {/* Info */}
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 mb-1">Brand Sentiment Analysis</h3>
            <p className="text-sm text-orange-400 mb-1">Domain: {data.domain}</p>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-700 mb-2">
              <span className="flex items-center gap-1">
                {data.overallSentiment === 'positive' ? <FaceSmileIcon className="w-4 h-4 text-green-700"/> : data.overallSentiment === 'negative' ? <FaceFrownIcon className="w-4 h-4 text-red-700"/> : <MinusIcon className="w-4 h-4 text-yellow-700"/>}
                Overall: <span style={{color: sentimentColor}} className="font-semibold capitalize">{data.overallSentiment}</span>
              </span>
              <span className="flex items-center gap-1">
                <TrendIcon className={`w-4 h-4 ${trendColor}`}/>
                Trend: <span className={`font-semibold capitalize ${trendColor}`}>{data.sentimentTrendMeasured === false ? 'Not measured' : data.sentimentTrend}</span>
              </span>
              <span>Confidence: <span className="font-semibold text-gray-900">{data.confidenceMeasured === false ? 'Not measured' : data.confidence + '%'}</span></span>
              <span>Sources: <span className="font-semibold text-gray-900">{data.mentionCount}</span></span>
            </div>
            <p className="text-sm text-gray-600 italic">{data.summary}</p>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-2">
            {distData.map(d => (
              <div key={d.name} className="bg-white/50 rounded-xl p-3 text-center border border-gray-300/30">
                <div className="text-lg font-bold" style={{color: d.color}}>{d.value}%</div>
                <div className="text-xs text-gray-600">{d.name}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div role="tablist" aria-label="Sentiment Analysis sections" className="flex gap-1 border-b border-gray-300/50">
        <button onClick={() => setActiveTab('overview')} className={tabClass('overview')} aria-label="Overview tab">
          <FaceSmileIcon className="w-4 h-4" aria-hidden="true"/> Overview
        </button>
        <button onClick={() => setActiveTab('risks')} className={tabClass('risks')} aria-label="Risks and Drivers tab">
          <ShieldExclamationIcon className="w-4 h-4" aria-hidden="true"/> Risks & Drivers
        </button>
        <button onClick={() => setActiveTab('recommendations')} className={tabClass('recommendations')} aria-label="Recommendations tab">
          <LightBulbIcon className="w-4 h-4" aria-hidden="true"/> Recommendations
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Emotional Tone Radar */}
          <div className="bg-gray-100/60 backdrop-blur rounded-2xl p-6 border border-gray-300/50">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Emotional Tone Profile</h4>
          {unmeasuredEmotions.length > 0 && (
            <p className="text-xs text-gray-500 italic mb-3">
              Not measured: {unmeasuredEmotions.join(', ')} &mdash; no source quote evidences these.
            </p>
          )}
            {emotionData.length >= 3 ? (
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={emotionData}>
                  <PolarGrid stroke="#374151"/>
                  <PolarAngleAxis dataKey="emotion" tick={{fill:'#d1d5db', fontSize: 14}}/>
                  <PolarRadiusAxis angle={90} domain={[0,100]} tick={{fill:'#d1d5db', fontSize: 14}}/>
                  <Radar name="Emotion" dataKey="value" stroke="#f97316" fill="#f97316" fillOpacity={0.2}/>
                  <Tooltip content={({active, payload, label}) => { if (active && payload && payload.length) { return (<div style={{backgroundColor:'#1f2937',border:'1px solid #374151',borderRadius:'8px',padding:'10px',color:'#fff'}}><p style={{color:'#fff',margin:0,fontWeight:'bold'}}>{payload[0].payload.emotion || label}</p>{payload.map((p,i) => (<p key={i} style={{color:'#fff',margin:'4px 0 0 0'}}>{p.name} : {p.value}</p>))}</div>); } return null; }}/>
                </RadarChart>
              </ResponsiveContainer>
            ) : emotionData.length > 0 ? (
            <div className="py-4 space-y-3">
              <p className="text-xs text-gray-500 italic">Only {emotionData.length} of 8 emotions have supporting quotes &mdash; too few for a radar, so they are listed instead.</p>
              {emotionData.map((e) => (
                <div key={e.emotion} className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">{e.emotion}</span>
                  <span className="font-semibold text-gray-900">{e.value}% of sources</span>
                </div>
              ))}
            </div>
          ) : <p className="text-gray-500 text-center py-8">No emotion could be measured from the sources</p>}
          </div>

          {/* Sentiment Distribution Donut */}
          <div className="bg-gray-100/60 backdrop-blur rounded-2xl p-6 border border-gray-300/50">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Sentiment Distribution</h4>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={distData} cx="50%" cy="50%" innerRadius={70} outerRadius={100} paddingAngle={3} dataKey="value">
                  {distData.map((entry, i) => <Cell key={i} fill={entry.color}/>)}
                </Pie>
                <Tooltip content={({active, payload}) => { if (active && payload && payload.length) { return (<div style={{backgroundColor:'#1f2937',border:'1px solid #374151',borderRadius:'8px',padding:'10px',color:'#fff'}}><p style={{color:'#fff',margin:0,fontWeight:'bold'}}>{payload[0].name}</p><p style={{color:'#fff',margin:'4px 0 0 0'}}>{payload[0].name} : {payload[0].value}%</p></div>); } return null; }}/>
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-6 mt-2">
              {distData.map(d => (
                <div key={d.name} className="flex items-center gap-2 text-sm">
                  <div className="w-3 h-3 rounded-full" style={{backgroundColor: d.color}}/>
                  <span className="text-gray-700">{d.name}: {d.value}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Sentiment Sources */}
          {data.sources && data.sources.length > 0 && (
            <div className="lg:col-span-2 bg-gray-100/60 backdrop-blur rounded-2xl p-6 border border-gray-300/50">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Sentiment Sources</h4>
              <div className="space-y-3">
                {data.sources.map((s, i) => (
                  <div key={i} className="flex items-center gap-4 bg-white/40 rounded-xl p-3 border border-gray-300/30">
                    <div aria-hidden="true" className={`w-3 h-3 rounded-full ${s.sentiment === 'positive' ? 'bg-green-500' : s.sentiment === 'negative' ? 'bg-red-500' : 'bg-yellow-500'}`}/>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 truncate">{s.title}</p>
                      <p className="text-xs text-gray-500">{s.source}</p>
                    </div>
                    <span role="status" aria-label={`Sentiment: ${s.sentiment}`} className={`text-xs px-2 py-1 rounded-full capitalize ${s.sentiment === 'positive' ? 'bg-green-500/20 text-green-700' : s.sentiment === 'negative' ? 'bg-red-500/20 text-red-700' : 'bg-yellow-500/20 text-yellow-700'}`}>
                      {s.sentiment}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'risks' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sentiment Drivers */}
          <div className="bg-gray-100/60 backdrop-blur rounded-2xl p-6 border border-gray-300/50">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Sentiment Drivers</h4>
            {driverData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={driverData} layout="vertical" margin={{left:20}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151"/>
                  <XAxis type="number" domain={[0,100]} tick={{fill:'#d1d5db'}}/>
                  <YAxis type="category" dataKey="name" tick={{fill:'#d1d5db', fontSize: 14}} width={120}/>
                  <Tooltip content={({active, payload, label}) => { if (active && payload && payload.length) { return (<div style={{backgroundColor:'#1f2937',border:'1px solid #374151',borderRadius:'8px',padding:'10px',color:'#fff'}}><p style={{color:'#fff',margin:0,fontWeight:'bold'}}>{label}</p>{payload.map((p,i) => (<p key={i} style={{color:'#fff',margin:'4px 0 0 0'}}>{p.name} : {p.value}</p>))}</div>); } return null; }}/>
                  <Bar dataKey="strength" radius={[0,4,4,0]}>
                    {driverData.map((entry, i) => <Cell key={i} fill={entry.fill}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-gray-500 text-center py-8">No driver data available</p>}
            <div className="space-y-2 mt-4">
              {(data.sentimentDrivers || []).map((d, i) => (
                <div key={i} className="flex items-start gap-3 text-sm">
                  <div className={`w-2 h-2 rounded-full mt-1 ${d.impact === 'positive' ? 'bg-green-500' : d.impact === 'negative' ? 'bg-red-500' : 'bg-yellow-500'}`}/>
                  <div>
                    <span className="text-gray-900 font-medium">{d.factor}:</span>
                    <span className="text-gray-600 ml-1">{d.description}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Reputation Risks */}
          <div className="bg-gray-100/60 backdrop-blur rounded-2xl p-6 border border-gray-300/50">
            <h4 className="text-lg font-semibold text-gray-900 mb-4" id="reputation-risks-heading">Reputation Risks</h4>
            <div className="space-y-4">
              {(data.reputationRisks || []).map((r, i) => (
                <div role="article" aria-label={`Risk: ${r.risk}, Severity: ${r.severity}`} key={i} className={`rounded-xl p-4 border ${r.severity === 'high' ? 'bg-red-500/10 border-red-500/30' : r.severity === 'medium' ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-blue-500/10 border-blue-500/30'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <ExclamationTriangleIcon aria-hidden="true" className={`w-5 h-5 ${r.severity === 'high' ? 'text-red-700' : r.severity === 'medium' ? 'text-yellow-700' : 'text-blue-700'}`}/>
                      <h5 className="text-gray-900 font-semibold">{r.risk}</h5>
                    </div>
                    <span role="status" aria-label={`Severity: ${r.severity}`} className={`text-xs px-2 py-1 rounded-full capitalize font-medium ${r.severity === 'high' ? 'bg-red-500/20 text-red-700' : r.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-700' : 'bg-blue-500/20 text-blue-700'}`}>
                      {r.severity}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{r.description}</p>
                </div>
              ))}
              {(!data.reputationRisks || data.reputationRisks.length === 0) && (
                <p className="text-gray-700 text-center py-8">No significant risks detected</p>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'recommendations' && (
        <div className="space-y-4">
          {(data.recommendations || []).map((r, i) => (
            <div key={i} className="bg-gray-100/60 backdrop-blur rounded-2xl p-5 border border-gray-300/50">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <LightBulbIcon className="w-6 h-6 text-yellow-700"/>
                  <h5 className="text-lg font-semibold text-gray-900">{r.action}</h5>
                </div>
                <span className={`text-xs px-3 py-1 rounded-full capitalize font-medium ${r.priority === 'high' ? 'bg-red-500/20 text-red-700' : r.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-700' : 'bg-green-500/20 text-green-700'}`}>
                  {r.priority} Priority
                </span>
              </div>
              <p className="text-gray-600 ml-9">{r.description}</p>
            </div>
          ))}
          {(!data.recommendations || data.recommendations.length === 0) && (
            <div className="text-gray-500 text-center py-8 bg-gray-100/60 rounded-2xl border border-gray-300/50">
              No recommendations available
            </div>
          )}
        </div>
      )}
    </div>
  );
}
